/**
 * Shared number/date formatting, es-UY. Added for Milestone 5 (Solicitudes y Mis cotizaciones);
 * reusable by whatever later milestone renders a price or a date next.
 */

export function formatCurrency(amount: number, currency = 'UYU'): string {
  return new Intl.NumberFormat('es-UY', { style: 'currency', currency }).format(amount);
}

const CALENDAR_DATE_FORMAT = { day: '2-digit', month: '2-digit', year: 'numeric' } as const;

/**
 * For a genuine instant (`createdAt`, `acceptedAt`) — the viewer's own time zone is the right read:
 * "when did this actually happen, here".
 */
export function formatDateTime(value: string | null | undefined): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat('es-UY', CALENDAR_DATE_FORMAT).format(date);
}

const SHORT_DATE_FORMAT = { day: 'numeric', month: 'short' } as const;

/**
 * Short "D MMM." form of a genuine instant (e.g. "14 ago."), for a spot that wants the day/month
 * but not a full calendar date — CotizacionesPage's "Aceptada el {date}" line. Same genuine-instant,
 * viewer-local-timezone read `formatDateTime` above documents (NOT the UTC convention
 * `formatCalendarDate` below uses): `acceptedAt` is exactly the kind of value that docblock already
 * covers, just rendered without the year.
 */
export function formatShortDate(value: string | null | undefined): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat('es-UY', SHORT_DATE_FORMAT).format(date);
}

/**
 * For a plain calendar date with no time-of-day meaning: `periodStart` (a SQL `date` column).
 * Formatted in UTC on purpose — it travels over JSON as midnight UTC of that day, and reading it
 * back in the viewer's own time zone (e.g. America/Montevideo, UTC-3) would print the day before.
 * Not for a quote's `validUntil`: that one is the end of a Montevideo day (`formatMontevideoDate`).
 */
export function formatCalendarDate(value: string | null | undefined): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat('es-UY', { ...CALENDAR_DATE_FORMAT, timeZone: 'UTC' }).format(date);
}

/**
 * NEX-1165 design pass — the mock's "1 set. 2026": the same plain calendar date as
 * `formatCalendarDate` (UTC, so the day never shifts), with the short month name.
 */
export function formatCalendarDateShort(value: string | null | undefined): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat('es-UY', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }).format(date);
}

const MINUTE_MS = 60_000;
const HOUR_MS = 3_600_000;
const DAY_MS = 86_400_000;

/**
 * Coarse "how long ago" read of a genuine instant (`createdAt`) for a glanceable list row, where
 * the exact timestamp (`formatDateTime`) is secondary to "is this fresh". Four buckets only,
 * es-UY, each coarser than the last: under an hour in minutes, under a day in hours, exactly one
 * day as "ayer", anything older in whole days — deliberately never a finer unit than the bucket a
 * value falls in (e.g. no "1 day and 3 hours"). A future-dated or unparsable `value` (clock skew,
 * bad data) clamps to the freshest bucket rather than printing a negative count.
 */
export function formatRelativeTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const diffMs = Math.max(0, Date.now() - date.getTime());

  if (diffMs < HOUR_MS) {
    const minutes = Math.max(1, Math.floor(diffMs / MINUTE_MS));
    return minutes === 1 ? 'hace 1 minuto' : `hace ${minutes} minutos`;
  }

  if (diffMs < DAY_MS) {
    const hours = Math.floor(diffMs / HOUR_MS);
    return hours === 1 ? 'hace 1 hora' : `hace ${hours} horas`;
  }

  const days = Math.floor(diffMs / DAY_MS);
  if (days === 1) return 'ayer';
  return `hace ${days} días`;
}

// --- America/Montevideo calendar days (NEX-1165, W2-hard-a) --------------------------------------

const MONTEVIDEO_TIME_ZONE = 'America/Montevideo';

/**
 * A quote's `validUntil` is the last instant of a day in America/Montevideo (nexvia-api-web
 * `domain/quote-defaults.ts`: "2026-12-31" is stored and sent back as "2027-01-01T02:59:59.999Z"),
 * so its UTC date part is the next day: it is always read in Montevideo, whatever the viewer's zone.
 */
export function formatMontevideoDate(value: string | null | undefined): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat('es-UY', { ...CALENDAR_DATE_FORMAT, timeZone: MONTEVIDEO_TIME_ZONE }).format(date);
}

/** The `YYYY-MM-DD` of `date` in Montevideo — what a date input holds and what the API reads. */
export function montevideoDateKey(date: Date): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: MONTEVIDEO_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((item) => item.type === type)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')}`;
}

/**
 * Today in Montevideo plus `days` calendar days, as `YYYY-MM-DD`: the quote form's prefilled
 * validity, the same day the API computes when the field is left out ("the end, in Uruguay, of the
 * day `quote_validity_days` after today there").
 */
export function montevideoDatePlusDays(days: number, now: Date = new Date(Date.now())): string {
  const [year, month, day] = montevideoDateKey(now).split('-').map(Number);
  const target = new Date(Date.UTC(year, month - 1, day + days));
  return target.toISOString().slice(0, 10);
}
